import type { LucideIcon } from 'lucide-react'
import { CORAL, FONT_QUICKSAND, GRAY_400, INK, RADIUS_BTN } from '../tokens'

export interface ModeOption<T extends string> {
  value: T
  label: string
  icon: LucideIcon
}

export interface ModeGroup<T extends string> {
  /** Short uppercase caption above this cluster (e.g. "Learn", "Practice",
   *  "Reference") -- the reason to group at all: pills that differ in kind
   *  (a learning mode vs. an answer format vs. a static reference sheet)
   *  read as one undifferentiated row without it. Omit for an ungrouped run. */
  label?: string
  options: ModeOption<T>[]
}

interface ModeSelectorProps<T extends string> {
  /** Flat row -- use for a single axis with no internal groups (e.g. the
   *  Foundation/Standard/Advanced/Competitive rigor tabs). */
  options?: ModeOption<T>[]
  /** Clustered rows with an optional caption per cluster -- use when the
   *  options mix genuinely different kinds of things (e.g. Explanation/Try
   *  Together are ways to *learn*, MCQ/Short/Long are answer *formats*,
   *  Cheat Sheet is *reference*). Groups with an empty `options` array
   *  should be filtered out by the caller before passing. */
  groups?: ModeGroup<T>[]
  active: T
  onChange: (value: T) => void
  disabled?: boolean
}

/**
 * The mode-label button row (Explanation / Try Together / MCQ / etc.) from
 * UnderstandSessionPage.tsx and TestPrepSessionPage.tsx -- pure UI, no data
 * dependency, so it's safe to de-duplicate out of both apps' copies of those
 * pages. Replaces raw emoji labels (📖🤝🔘✏️📝) with lucide icons and one
 * flat selected-state (coral fill) instead of the previous purple/coral mix.
 */
export function ModeSelector<T extends string>({ options, groups, active, onChange, disabled }: ModeSelectorProps<T>) {
  function renderButton(opt: ModeOption<T>) {
    const isActive = opt.value === active
    const Icon = opt.icon
    return (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        disabled={disabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5em',
          padding: '0.6rem 1.1rem',
          borderRadius: RADIUS_BTN,
          fontFamily: FONT_QUICKSAND,
          fontWeight: 700,
          fontSize: '0.85rem',
          border: isActive ? 'none' : `2px solid ${INK}`,
          background: isActive ? CORAL : '#fff',
          color: isActive ? '#fff' : INK,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        <Icon size={16} />
        {opt.label}
      </button>
    )
  }

  if (groups) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem' }}>
        {groups.map((group, i) => (
          <div key={group.label ?? i} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {group.label && (
              <span
                style={{
                  fontFamily: FONT_QUICKSAND,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: GRAY_400,
                }}
              >
                {group.label}
              </span>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
              {group.options.map(renderButton)}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
      {(options ?? []).map(renderButton)}
    </div>
  )
}
